// src/main.jsx
import React, { StrictMode, Suspense } from "react";
import { createRoot } from "react-dom/client";
import "./index.css";
import "./styles/mobile-layout.css";
import "/src/lib/quietConsole.js";

// Only enable tap probe in dev or when explicitly requested (?tap=1)
try {
  const url = new URL(window.location.href);
  const tapFlag = url.searchParams.get("tap");
  if (import.meta.env.DEV || tapFlag === "1") {
    await import("/src/lib/tapProbe.js");
  }
} catch { /* no-op */ }

// Optional: lightweight click delegation (safe in prod)
import "/src/lib/clickDelegation.js";

// Load the Google tag before anything else touches analytics. Gated on a
// production build, an exact production hostname, and a valid measurement id;
// fails off otherwise. See lib/googleTag.js for why it is not in index.html.
import { loadGoogleTag } from "./lib/googleTag.js";
try {
  loadGoogleTag();
} catch (e) {
  console.warn("[Boot] Google tag skipped:", e);
}

// Initialize analytics (guarded: PROD + key required)
import { initAnalytics } from "./lib/analytics.js";
try {
  initAnalytics();
} catch (e) {
  console.warn("[Boot] analytics init skipped:", e);
}

// Guarded auth provider
import { AuthGate } from "./lib/supabase/AuthGate.jsx";

// Enforce preload policy (don’t let an exception crash boot).
//
// NOT awaited. This used to be a top-level `await`, which put a 17 KB module in
// front of the 508 KB one the whole page is waiting for: measured on production
// 2026-08-04, Pixel 7 at 390x844, 1.6 Mbps, 4x CPU, this module finished at
// 477 ms, mobilePerformance occupied 973–1,160 ms, and App-*.js was not even
// REQUESTED until 1,167 ms — 690 ms of a 4,787 ms wait spent not downloading the
// thing being waited for (#1232).
//
// enforceSafePreloads only rewrites <link rel=preload> tags the document already
// carries; nothing below reads its result, and nothing it does is required for
// the app to mount. Running it concurrently lets the App request go out first.
import("./lib/mobilePerformance")
  .then((m) => m.enforceSafePreloads?.())
  .catch((e) => console.warn("[Boot] enforceSafePreloads skipped:", e));

// Minimal boot overlay helper (works with an existing #boot element)
function markBootOK() {
  const boot = document.getElementById("boot");
  if (boot) boot.classList.add("ok");
}

// LOAD THE APPLICATION BEFORE MOUNTING IT.
//
// This used to render a <BootLoader/> that returned `null` while it imported
// App.jsx from inside an effect. createRoot() replaces whatever is already in
// its container on the first render, so on a prerendered page that null render
// DELETED the real markup — measured on production 2026-08-04 (Pixel 7,
// 390x844, 1.6 Mbps, 4x CPU): /ats-resume-checker held 6,998 characters of
// article at 411 ms, #root was empty by 1,294 ms, and content did not return
// until 5,355 ms. Four seconds of nothing, on the best-engaging landing page
// the product has.
//
// Awaiting the import first means #root keeps its prerendered content until
// there is equivalent content to put in its place. Every route component in
// App.jsx is a static import, so once this resolves the router paints
// immediately rather than suspending again.
//
// This is a top-level await, which this module already uses (see
// mobilePerformance above); it does not change how App.jsx is chunked.
let App = null;
let bootError = null;
try {
  const mod = await import("./App.jsx");
  App = mod.default || mod.App || null;
  if (!App) throw new Error("App.jsx exported no component");
} catch (e) {
  bootError = e;
  console.error("[Boot] App import failed", e);
  // Re-shows the boot overlay and writes the reason into #boot-log.
  try { window.__bootFail?.(e); } catch { /* no-op */ }
}

function BootFailure({ error }) {
  return (
    <div style={{ color: "#eee", padding: 16 }}>
      <div style={{ fontWeight: 700, marginBottom: 8 }}>App failed to load</div>
      <pre style={{ whiteSpace: "pre-wrap" }}>{String(error?.stack || error?.message || error)}</pre>
    </div>
  );
}

const rootEl = document.getElementById("root");
if (App) {
  createRoot(rootEl).render(
    <StrictMode>
      <AuthGate>
        <Suspense fallback={null}>
          <App />
        </Suspense>
      </AuthGate>
    </StrictMode>
  );
  markBootOK();
} else {
  // Leave the overlay up: __bootFail has already made it visible with the reason.
  createRoot(rootEl).render(<BootFailure error={bootError} />);
}

// Emit a separate prod-only chunk for analytics-lite
if (import.meta.env.PROD) {
  import('./lib/analytics-lite')
    .then(m => m.init?.())
    .catch(() => { /* no-op */ });
}
